import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { AriaLive } from "../AriaLive";
import { Grid } from "../Grid";
import { TransitionBlock, transitionBlockPropTypes } from "../TransitionBlock";
import styles from "./ListMatrix.module.scss";

const CASCADE_DELAY = 32;
const GRID_MULTIPLIER = 3;
const TRANSITION_IN = { duration: 250, transformations: ["fadeReset"] };
const TRANSITION_INITIAL = {
  duration: 0.001,
  transformations: [{ action: "opacity", amount: "0.01" }],
};
const TRANSITION_OUT = { duration: 150, transformations: ["fade"] };

/**
 * ListMatrix displays items in a list and optionally lays them out according to a grid. This
 * component works well when there one or more lines of items to be displayed, particularly if a
 * grid layout is needed. To display items vertically or horizontally, see List, ListPaginator or
 * ListScroller, which also provide scrolling capabilities.
 *
 * Note: When changing the items list after initial render and the new list won't cause a
 * re-layout because the size of the rendered items won't change, it may be necessary to give
 * a new animationToken to force a re-render.
 */

export const ListMatrix = (props) => {
  const {
    animate = true,
    animationToken,
    center,
    className,
    horizontalSeparator,
    itemClassName,
    items,
    layout = "grid",
    numberOfColumns,
    onTransformationEnd,
    separator,
  } = props;
  const { gridColumnSpacing, screenSize } = useDeviceInfo();
  const [initialRender, setInitialRender] = useState();
  const columnInfo = getPlacementInfo();
  const Component = layout === "grid" ? Grid : "div";

  useEffect(() => {
    setInitialRender(true);
  }, []);

  return <Component {...getProps()}>{renderItems()}</Component>;

  function renderItems() {
    let content = undefined;

    if (columnInfo?.length) {
      content = columnInfo.map(renderItem);
    }

    return content;
  }

  function renderItem(itemInfo, index) {
    const item = items[itemInfo.index];
    const isComponentType = item?.type === "component";

    return (
      <div key={itemInfo.index} {...getListItemProps(itemInfo)}>
        {isComponentType ? (
          renderTransitionContent(itemInfo, index, item)
        ) : (
          <AriaLive className={styles.aria} hiddenFromScreen={false}>
            {renderTransitionContent(itemInfo, index, item)}
          </AriaLive>
        )}
      </div>
    );
  }

  function renderTransitionContent(itemInfo, index, item) {
    return animate ? (
      <TransitionBlock {...getTransitionBlockProps(itemInfo, index)}>
        <div {...getItemProps(itemInfo)}>{itemInfo.index !== undefined && item.content}</div>
      </TransitionBlock>
    ) : (
      <div {...getItemProps(itemInfo)}>{itemInfo.index !== undefined && item.content}</div>
    );
  }

  function getProps() {
    const columnCount = getTotalColumnsInRow();

    return {
      className: classNames(className, styles.container, {
        [styles.center]: center,
        [styles.layoutNone]: layout === "none",
      }),
      ...(layout === "grid" && {
        gutterSpacing: gridColumnSpacing,
        numberOfColumns: columnCount === 1 ? 1 : columnCount * GRID_MULTIPLIER,
      }),
    };
  }

  function getTransitionBlockProps(itemInfo, index) {
    const delay = { delay: index * CASCADE_DELAY };
    const token = `${getTotalColumnsInRow()}-${index}-${itemInfo.row}-${
      initialRender ? animationToken : ""
    }`;

    return {
      alwaysFireTransformationEnd: true,
      animationToken: token,
      className: styles.transitionContainer,
      onTransformationEnd: itemInfo.index === items.length - 1 ? onTransformationEnd : undefined,
      transitionIn: { ...TRANSITION_IN, ...delay },
      transitionInitial: TRANSITION_INITIAL,
      transitionOut: { ...TRANSITION_OUT, ...delay },
    };
  }

  function getListItemProps(itemInfo) {
    const { numberSpacesForItem } = itemInfo;
    const multiplier = screenSize === "small" ? 1 : GRID_MULTIPLIER;

    return {
      className: classNames(styles.item, itemClassName, styles.row, {
        [styles.lastRow]: itemInfo.row === getNumberOfRows(columnInfo) - 1,
      }),
      ...(layout === "grid" && {
        style: { gridColumn: `span ${numberSpacesForItem * multiplier}` },
      }),
    };
  }

  function getItemProps(itemInfo) {
    return {
      className: classNames({
        [styles.horizontalSeparator]: shouldShowHorizontalSeparator(itemInfo),
        [styles.verticalSeparator]: shouldShowVerticalSeparator(itemInfo),
      }),
      ...(layout === "grid" && { style: getStyle(itemInfo) }),
    };
  }

  function shouldShowHorizontalSeparator(itemInfo) {
    const totalRows = getNumberOfRows(columnInfo);
    const isLastRow = itemInfo.row + 1 === totalRows;

    return horizontalSeparator && !isLastRow;
  }

  function shouldShowVerticalSeparator(itemInfo) {
    return separator && !itemInfo.last;
  }

  function getStyle(item) {
    const { index, last } = item;

    return {
      flexGrow: 1,
      height: "100%",
    };
  }

  function getPlacementInfo() {
    const columnsInRow = getTotalColumnsInRow();
    const numberOfItems = items ? items.length : 0;
    const placementData = [];
    let currentRowNumber = 0;
    let index;
    let spacesFilledCurrentRow = 0;

    for (index = 0; index < numberOfItems; index += 1) {
      const currentItem = items[index];
      const numberSpacesForItem = getNumberSpacesForItem(currentItem.preferredWidth);
      const info = { index, numberSpacesForItem, row: currentRowNumber };

      if (spacesFilledCurrentRow + numberSpacesForItem <= columnsInRow) {
        spacesFilledCurrentRow += numberSpacesForItem;

        if (spacesFilledCurrentRow === columnsInRow) {
          info.last = true;
          currentRowNumber += 1;
          spacesFilledCurrentRow = 0;
        }
      } else {
        currentRowNumber += 1;
        info.row = currentRowNumber;
        spacesFilledCurrentRow = numberSpacesForItem;
      }

      placementData.push(info);
    }

    return placementData;
  }

  function getNumberSpacesForItem(preferredWidth) {
    return Math.min(
      layout === "none" || !preferredWidth ? 1 : preferredWidth,
      getTotalColumnsInRow()
    );
  }

  function getNumberOfRows(info) {
    const allRowNumbers = removeEmptyItems(info).map((item) => item.row);

    return Math.max(...allRowNumbers) + 1;
  }

  function getTotalColumnsInRow() {
    return screenSize === "small" ? 1 : numberOfColumns || (screenSize === "medium" ? 3 : 4);
  }

  function removeEmptyItems(itemsList) {
    return itemsList.filter((item) => item !== undefined);
  }
};

ListMatrix.displayName = "ListMatrix";

ListMatrix.propTypes = {
  /** Indicates whether to animate the items. If true, TransitionBlock will transition items. */
  animate: PropTypes.bool,

  /** Unique id that forces items to re-render. See TransitionBlock for details. */
  animationToken: transitionBlockPropTypes.animationToken,

  /** Whether to center the component. */
  center: PropTypes.bool,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Option to provide horizontal separation indicator (border) between rows. */
  horizontalSeparator: PropTypes.bool,

  /** className that will be applied to each item. */
  itemClassName: PropTypes.string,

  /** Complete list of items to be rendered. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      preferredWidth: PropTypes.number,
      type: PropTypes.string,
    })
  ),

  /**
   * When displaying items, the items can be laid out in a grid that's appropriately defined for
   * each device size. Only the values used for gaps between items will utilize grid values. The
   * item width will be determined by whatever space is left over. If the numberOfColumns specified
   * match the count of columns appropriate for the device, then the items will match standard grids.
   * If layout is none, all item sizes and horizontal gaps between items must be defined by the
   * calling component.
   */
  layout: PropTypes.oneOf(["grid", "none"]),

  /**
   * Number of columns. One will always be applied for small (mobile) devices. If numberOfColumns not
   * given, the number of columns will be deduced from the device size.
   */
  numberOfColumns: PropTypes.number,

  /**
   * Function called when the items' transition is complete after initial render on when items are
   * changed.
   */
  onTransformationEnd: transitionBlockPropTypes.onTransformationEnd,

  /** className that will be applied to each row. */
  rowClassName: PropTypes.string,

  /** Option to provide vertical separation indicator (border) between items. */
  separator: PropTypes.bool,
};
